import { useDispatch, useSelector } from "react-redux";
import { setDraggingComponent } from "@/store/iframeSlice";
import {
  CursorType,
  ModalType,
  setCursorType,
  setModal,
  setSelectedTypographyOption,
} from "@/store/globalSettingsSlice";
import React, { useMemo } from "react";
import useTypographyPresets, {
  TypographyPreset,
} from "@/components/AppHeader/hooks/useTypographyPresets";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Check } from "lucide-react";
import { createDummyComponent } from "@/components/AppHeader/utils";
import { useActiveProject } from "@/utils/utils";
import { setTempoClipboardDataOnDrag } from "@/components/Canvas/utils/canvasClipboardData";
import {
  DEFAULT_STORYBOARD_TEXT_HEIGHT,
  DEFAULT_STORYBOARD_TEXT_WIDTH,
} from "@/components/Canvas/utils";

const TypographyOptions = React.memo(() => {
  const { options } = useTypographyPresets();
  const dispatch = useDispatch();

  const selectedTypographyOption = useSelector(
    (state: any) => state.globalSettings.selectedTypographyOption,
  );

  const project = useActiveProject();

  const selectedTypographyOptionToUse = useMemo(() => {
    return selectedTypographyOption || options[0];
  }, [selectedTypographyOption, options]);

  const handleOnDragStart = (e, option: TypographyPreset) => {
    e.dataTransfer.dropEffect = "move";

    dispatch(setDraggingComponent("typography"));
    const fileContents = createDummyComponent(
      option.jsxString,
      project.framework,
    );
    const htmlForInstantUpdate = option.htmlInstantUpdate;

    setTempoClipboardDataOnDrag(e, {
      htmlForInstantUpdate,
      fileContents,
      insertIntoHoveredElement: true,
      width: DEFAULT_STORYBOARD_TEXT_WIDTH,
      height: DEFAULT_STORYBOARD_TEXT_HEIGHT,
    });
  };

  const handleOnClick = (option: TypographyPreset) => {
    dispatch(setSelectedTypographyOption(option));
  };

  if (!options.length) {
    return (
      <DropdownMenuContent
        onClick={() => {
          dispatch(
            setCursorType({
              cursorType: CursorType.Text,
            }),
          );
        }}
        className="w-[400px] overflow-x-auto grow flex-col pointer-events-auto p-2.5 text-secondary-900 text-xs flex gap-y-2"
      >
        <p>
          To set up typography presets, add them to the{" "}
          <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-2xs font-semibold">
            tempo.config.json
          </code>
          .
        </p>
        <p>Typography options are specified under the "typography" key:</p>
        <pre className="bg-secondary-100 px-2 py-1 rounded font-mono text-2xs max-w-full overflow-x-auto text-secondary-800">
          {`{
  ...otherConfig,
  "typography": [
    {
      "name": "Header One", // Name of the style
      "preview": "Aa", // Text used in preview swatch
      "tag": "p", // Tag to use
      "classes": ["leading-7", "font-bold"] // Tailwind classes to apply
    }
  ]
}`}
        </pre>
      </DropdownMenuContent>
    );
  }

  return (
    <DropdownMenuContent
      onClick={() => {
        dispatch(
          setCursorType({
            cursorType: CursorType.Text,
          }),
        );
      }}
      className="w-[250px] overflow-auto max-h-[70vh]"
    >
      {options.map((option, index) => {
        return (
          <DropdownMenuItem
            key={index}
            className="cursor-grab p-0.5 text-nowrap text-xs font-semibold flex items-center flex-row gap-2"
            draggable
            onDragStart={(e) => handleOnDragStart(e, option)}
            onDragEnd={() => {
              dispatch(setCursorType({ cursorType: CursorType.Pointer }));
              dispatch(setModal(ModalType.None));
              dispatch(setDraggingComponent(false));
            }}
            onClick={() => handleOnClick(option)}
          >
            <div className="bg-secondary-200 size-11 min-w-11 rounded-md flex justify-center items-center">
              {option.preview}
            </div>
            <div className="flex items-center justify-between w-full pr-2">
              {option.name}
              {option.name === selectedTypographyOptionToUse?.name && (
                <Check size="14" />
              )}
            </div>
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuContent>
  );
});

export default TypographyOptions;
